// Connect your Google/Outlook calendar so approved leave syncs automatically.
// Renders nothing unless the integration is configured server-side (feature flag),
// so it stays invisible until OAuth credentials are added to leave-service.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { CalendarCheck, Loader2, Link2, Check, Bell } from 'lucide-react';
import {
  getCalendarSyncStatus, getCalendarConnectUrl, disconnectCalendar, type CalendarSyncStatus,
  getCalendarReminderPrefs, setCalendarReminderPrefs, type CalendarReminderPrefs,
} from '@/lib/hrApi';

const PROVIDER_LABEL: Record<string, string> = { google: 'Google Calendar', microsoft: 'Outlook' };

const OFFSET_OPTIONS = [60, 30, 15, 5];
const CHANNEL_OPTIONS: { id: string; label: string }[] = [
  { id: 'push', label: 'Push' }, { id: 'in_app', label: 'In-app' }, { id: 'email', label: 'Email' },
];
const offsetLabel = (m: number) =>
  m >= 60 ? `${m / 60} hour${m >= 120 ? 's' : ''} before` : `${m} minutes before`;

// KaziPeople's own reminders for synced external meetings (independent of the
// provider's notifications). Only shown when a calendar is connected.
const ReminderPrefsSection: React.FC = () => {
  const [prefs, setPrefs] = useState<CalendarReminderPrefs | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getCalendarReminderPrefs().then(setPrefs).catch(() => setPrefs(null)); }, []);
  if (!prefs) return null;

  const save = async (next: CalendarReminderPrefs) => {
    setPrefs(next); // optimistic
    setSaving(true);
    try { setPrefs(await setCalendarReminderPrefs(next)); } finally { setSaving(false); }
  };
  const toggleOffset = (m: number) => save({
    ...prefs,
    offsets: prefs.offsets.includes(m)
      ? prefs.offsets.filter((x) => x !== m)
      : [...prefs.offsets, m].sort((a, b) => b - a),
  });
  const toggleChannel = (c: string) => save({
    ...prefs,
    channels: prefs.channels.includes(c) ? prefs.channels.filter((x) => x !== c) : [...prefs.channels, c],
  });

  return (
    <div className="mt-4 pt-4 border-t border-border space-y-3">
      <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
        <input type="checkbox" className="accent-orange-500 w-4 h-4"
          checked={prefs.enabled} onChange={() => save({ ...prefs, enabled: !prefs.enabled })} />
        <Bell className="w-4 h-4 text-muted-foreground" /> External meeting reminders
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </label>
      {prefs.enabled && (
        <div className="pl-6 space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Remind me</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {OFFSET_OPTIONS.map((m) => (
                <label key={m} className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                  <input type="checkbox" className="accent-orange-500 w-4 h-4"
                    checked={prefs.offsets.includes(m)} onChange={() => toggleOffset(m)} />
                  {offsetLabel(m)}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Channels</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {CHANNEL_OPTIONS.map((c) => (
                <label key={c.id} className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                  <input type="checkbox" className="accent-orange-500 w-4 h-4"
                    checked={prefs.channels.includes(c.id)} onChange={() => toggleChannel(c.id)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const CalendarSyncCard: React.FC = () => {
  const { t } = useTranslation('timeoff');
  const [status, setStatus] = useState<CalendarSyncStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => getCalendarSyncStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { void load(); }, []);

  // Hidden until an admin configures the integration server-side.
  if (!status || !status.enabled) return null;

  const connect = async (provider: string) => {
    setBusy(true);
    try {
      const url = await getCalendarConnectUrl(provider);
      window.location.href = url; // provider OAuth, returns to the callback page
    } catch {
      setBusy(false);
    }
  };

  // Switch to a different provider without making the user disconnect by hand.
  // Fetch the new provider's OAuth URL FIRST (no mutation), then drop the current
  // connection — so a failed URL fetch never leaves them disconnected — then hand
  // off to OAuth. Disconnect clears the old outbound links + inbound events, so
  // leave re-syncs cleanly to the newly connected calendar.
  const switchTo = async (provider: string) => {
    setBusy(true);
    try {
      const url = await getCalendarConnectUrl(provider);
      await disconnectCalendar();
      window.location.href = url;
    } catch {
      await load().catch(() => undefined);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await disconnectCalendar(); await load(); } finally { setBusy(false); }
  };

  // Providers available to switch to (everything configured except the current one).
  const otherProviders = (status.providers || []).filter((p) => p !== status.provider);

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader className="rounded-t-xl">
        <CardTitle className="flex items-center gap-2 text-orange-700">
          <CalendarCheck className="w-5 h-5" /> {t('sync.title', 'Sync to your calendar')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {status.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-success">
              <Check className="w-4 h-4" />
              {t('sync.connected', 'Connected to {{provider}}', { provider: PROVIDER_LABEL[status.provider || ''] || status.provider })}
            </div>
            <p className="text-xs text-muted-foreground">{t('sync.connected_desc', 'Your approved leave appears on your calendar automatically.')}</p>
            {status.account_email && (
              <p className="text-xs text-muted-foreground">
                {t('sync.connected_as', 'Connected as {{email}} — join video calls with this account to be the host.', { email: status.account_email })}
              </p>
            )}
            <button
              onClick={disconnect}
              disabled={busy}
              className="h-9 px-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t('sync.disconnect', 'Disconnect')}
            </button>
            {otherProviders.length > 0 && (
              <div className="pt-1">
                <p className="text-xs text-muted-foreground mb-1.5">
                  {t('sync.switch_hint', 'Prefer a different calendar? Switch without disconnecting first.')}
                </p>
                <div className="flex flex-col gap-2">
                  {otherProviders.map((p) => (
                    <button
                      key={p}
                      onClick={() => switchTo(p)}
                      disabled={busy}
                      className="h-9 px-3 rounded-lg border border-border hover:border-orange-300 text-foreground text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Link2 className="w-4 h-4" /> {t('sync.switch_to', 'Switch to {{provider}}', { provider: PROVIDER_LABEL[p] || p })}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <ReminderPrefsSection />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('sync.desc', 'Connect your calendar so approved leave and holidays show up automatically.')}</p>
            <div className="flex flex-col gap-2">
              {status.providers.map((p) => (
                <button
                  key={p}
                  onClick={() => connect(p)}
                  disabled={busy}
                  className="h-10 px-3 rounded-lg border border-border hover:border-orange-300 text-foreground text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Link2 className="w-4 h-4" /> {t('sync.connect', 'Connect')} {PROVIDER_LABEL[p] || p}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarSyncCard;
